import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Collapse,
  Badge,
} from "react-bootstrap";
import toast from "react-hot-toast";
import { getUserData } from "../pages/const";
import statusColors from "../utils/statusColors";
import {
  getSupplier,
  isErpApiSuccess,
  listBankAccounts,
  listProductGroups,
  listSuppliers,
  receiveCustomOrderItem,
  receivedAndUpdateRepairingOrderItem,
} from "../erpClient";
import {
  getTodayDateString,
  getReceiveDateInputProps,
  validateReceiveDate,
} from "../utils/receiveDateInput";

function ReceiveUpdateModal({
  show,
  onHide,
  orderData,
  onReceiveSuccess,
  masterGoldRate = 0,
  masterSilverRate = 0,
}) {
  const [karigars, setKarigars] = useState([]);
  const [loading, setLoading] = useState(false);
  const [receiveDate, setReceiveDate] = useState(getTodayDateString);

  // Product groups and receive data
  const [productGroups, setProductGroups] = useState([]);
  const [itemReceiveData, setItemReceiveData] = useState({});
  const [productGroupNames, setProductGroupNames] = useState({});

  // Bank accounts and payment modes
  const [bankAccounts, setBankAccounts] = useState([]);
  const [goldPaymentMode, setGoldPaymentMode] = useState("Gold");
  const [goldBankAccount, setGoldBankAccount] = useState(null);
  const [silverPaymentMode, setSilverPaymentMode] = useState("Silver");
  const [silverBankAccount, setSilverBankAccount] = useState(null);

  // Live rate is always enabled
  const showLiveRate = true;

  // Payment mode options for React Select
  const PAYMENT_MODE_OPTIONS = [
    { value: "Cash", label: "Cash" },
    { value: "UPI", label: "UPI" },
    { value: "Bank Transfer", label: "Bank Transfer" },
    { value: "Cheque", label: "Cheque" },
    { value: "Card", label: "Card" },
  ];

  // Utility for per gram rate calculation (consistent with Karigar.jsx)
  const calculatePerGramRate = (rate) => rate / 10;
  const calculatePerGramSilverRate = (rate) => rate / 1000;

  // Metal payment mode options for Metal in Metal
  const METAL_PAYMENT_MODE_OPTIONS = [
    { value: "Gold", label: "Gold" },
    { value: "Silver", label: "Silver" },
  ];

  // Settle Payment states
  const [settlePaymentOpen, setSettlePaymentOpen] = useState({});
  const [settlePaymentOptions, setSettlePaymentOptions] = useState({});

  // Listen for API URL changes and close modal
  useEffect(() => {
    const handleApiUrlChange = (event) => {
      console.log('API URL changed in ReceiveUpdateModal, closing modal...', event.detail);
      if (show) {
        toast('Database switched. Closing modal.');
        onHide();
      }
    };

    window.addEventListener('apiUrlChanged', handleApiUrlChange);
    
    return () => {
      window.removeEventListener('apiUrlChanged', handleApiUrlChange);
    };
  }, [show, onHide]);

  useEffect(() => {
    if (show) {
      setReceiveDate(getTodayDateString());
    }
  }, [show]);

  // Fetch karigars and product groups on component mount
  useEffect(() => {
    if (show) {
      const loadData = async () => {
        // Extract karigar IDs from order data
        const karigarIds = orderData?.custom_order_entry
          ? orderData.custom_order_entry
              .map((item) => item.karigar_id)
              .filter((id) => id) // Remove null/undefined IDs
              .filter((id, index, array) => array.indexOf(id) === index) // Remove duplicates
          : [];

        const fetchedKarigars = await fetchKarigars(karigarIds);
        await fetchProductGroups(fetchedKarigars);
        await fetchBankDetails();
      };
      loadData();
    }
  }, [show]);

  const fetchKarigars = async (karigarIds = []) => {
    try {
      let suppliers = [];
      if (karigarIds.length > 0) {
        const results = await Promise.all(
          karigarIds.map((id) => getSupplier({ id: Number(id) })),
        );
        suppliers = results
          .filter((r) => r?.success && r?.data?.supplier)
          .map((r) => r.data.supplier);
      } else {
        const response = await listSuppliers();
        suppliers =
          response?.success && Array.isArray(response.data?.suppliers)
            ? response.data.suppliers
            : [];
      }

      if (suppliers.length > 0) {
        const fetchedKarigars = suppliers.map((karigar) => ({
          id: karigar.id,
          fullName: karigar.name || "",
          companyName: karigar.company_name || "",
          shortCode: karigar.short_code || "",
          contactNumber: karigar.contact_number || "",
          email: karigar.email || "",
          address: karigar.address || "",
          city: karigar.city || "",
          gstNumber: karigar.gst_number || karigar.GST_number || "",
          status: karigar.status || "",
          isPrimary:
            karigar.primary_supplier === 1 || karigar.primary_supplier === "1"
              ? 1
              : 0,
          gold: "0",
          silver: "0",
          balance: "0",
        }));

        // Sort by ID in descending order (recent first)
        fetchedKarigars.sort((a, b) => b.id - a.id);
        setKarigars(fetchedKarigars);
        return fetchedKarigars; // Return the karigars for use in fetchProductGroups
      } else {
        toast.error("Failed to fetch karigars");
        return [];
      }
    } catch (error) {
      console.error("Error fetching karigars:", error);
      toast.error("Failed to fetch karigars");
      return [];
    }
  };

  const fetchBankDetails = async () => {
    try {
      const response = await listBankAccounts();

      if (response?.success && Array.isArray(response.data?.bank_accounts)) {
        const fetchedBankDetails = response.data.bank_accounts.map((bank) => ({
          id: bank.id,
          bankName: bank.bank_name || "",
          accountHolder: bank.holder_name || "",
          accountType: bank.account_type || "",
          ifscCode: bank.ifsc_code || bank.IFSC_code || "",
          accountNumber: bank.account_number || "",
          openingBalance: bank.opening_balance || "",
          balance:
            bank.available_balance !== null &&
              bank.available_balance !== undefined
              ? bank.account_type === "metal"
                ? `${bank.available_balance} gm`
                : `₹${bank.available_balance}`
              : bank.account_type === "metal"
                ? "0 gm"
                : "₹0",
          status: bank.status === 1 ? "Active" : "Inactive",
          statusClass:
            bank.status === 1
              ? "bg-success-subtle text-success"
              : "bg-danger-subtle text-danger",
          useForInvoice: bank.invoice === 1,
          useForPOS: bank.pos === 1,
        }));

        fetchedBankDetails.sort((a, b) => b.id - a.id);
        setBankAccounts(fetchedBankDetails);
      } else if (response?.success && !response.data?.bank_accounts?.length) {
        setBankAccounts([]);
        toast("No bank accounts found");
      } else if (response && response.success === false) {
        setBankAccounts([]);
        toast.error(response.message || "Failed to fetch bank details");
      } else {
        setBankAccounts([]);
        toast.error("Unexpected response format from server");
      }
    } catch (error) {
      setBankAccounts([]);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch bank details"
      );
    }
  };

  const handleReceiveUpdate = async () => {
    // Check if at least one item has receive data
    const hasAnyReceiveData = Object.values(itemReceiveData).some(
      (data) => data.ornament_weight || data.purity || data.charged_amount
    );
    if (!hasAnyReceiveData) {
      toast.error("Please add receive data for at least one item");
      return;
    }

    const receiveDateError = validateReceiveDate(receiveDate, setReceiveDate);
    if (receiveDateError) {
      toast.error(receiveDateError);
      return;
    }

    setLoading(true);
    try {
      // Process each item receive data
      const receivePromises = Object.entries(itemReceiveData).map(
        async ([itemId, receiveData]) => {
          if (
            !receiveData.ornament_weight &&
            !receiveData.purity &&
            !receiveData.charged_amount
          ) {
            return null; // Skip items without receive data
          }

          // Find the item data to get the assigned karigar
          const itemData = productGroups.find(
            (group) => group.id.toString() === itemId.toString()
          );
          if (!itemData || !itemData.assigned_karigar_id) {
            toast.error(
              `Item ${
                itemData?.product_group || itemId
              } does not have an assigned karigar`
            );
            return null;
          }

          const userData = getUserData();

          // Calculate payable weight using 24k weight + labor + wastage
          const ornamentWeight = parseFloat(receiveData.ornament_weight || 0);
          const purity = parseFloat(receiveData.purity || 0);
          const laborPercentage = parseFloat(receiveData.labor_percentage || 0);
          const wastagePercentage = parseFloat(
            receiveData.wastage_percentage || 0
          );

          // Calculate the actual displayed payable weight value (same logic as in the form)
          let displayedPayableWeight = 0;
          if (
            ornamentWeight &&
            purity &&
            (laborPercentage || wastagePercentage)
          ) {
            // Calculate 24k weight first
            const goldWeight24k = (ornamentWeight * purity) / 100;
            // Then add labor and wastage percentages to the 24k weight
            displayedPayableWeight = parseFloat(
              (
                goldWeight24k *
                (1 + (laborPercentage + wastagePercentage) / 100)
              ).toFixed(2)
            );
          } else if (ornamentWeight && purity) {
            // Just 24k weight without labor/wastage
            displayedPayableWeight = parseFloat(
              ((ornamentWeight * purity) / 100).toFixed(3)
            );
          }

          // Determine settlement type based on settle payment options
          let settlementType = "AIA"; // Default to Amount in Amount for repairing orders
          let metalType = ""; // No default - should be set based on selection
          let bankType = "";
          let payMode = "";
          let payWt = ""; // Weight value from settlement table
          let payAmt = ""; // Amount value from settlement table

          // Check if settle payment is selected and get the options
          if (settlePaymentOpen[itemId] && settlePaymentOptions[itemId]) {
            const options = settlePaymentOptions[itemId];

            // console.log(`🔍 Settlement options for item ${itemId}:`, options);

            // For repairing orders, only handle Amount in Amount (AIA)
            if (orderData?.isRepairingOrder) {
              if (options.amountInAmountAmount) {
                settlementType = "AIA"; // Amount in Amount
                metalType = ""; // No metal type for Amount in Amount
                bankType = options.amountInAmountPaidFrom || "";
                payMode = options.amountInAmountPaymentMode || "";
                payWt = ""; // No weight for Amount in Amount
                payAmt = options.amountInAmountAmount || ""; // Amount from settlement table
                // console.log(
                //   `✅ AIA detected for repairing order: payMode=${payMode}, payAmt=${payAmt}, bankType=${bankType}`
                // );
              } else {
                // Default case when no settlement payment is selected for repairing orders
                settlementType = "AIA";
                metalType = ""; // No metal type for Amount in Amount
                payWt = "";
                payAmt = "";
                // console.log(
                //   `⚠️ No settlement options selected for repairing order, defaulting to AIA`
                // );
              }
            } else {
              // For non-repairing orders, handle all settlement types
              // Check Metal in Metal first (MIM)
              if (options.metalInMetalType && options.metalInMetalWeight) {
                settlementType = "MIM"; // Metal in Metal
                metalType = options.metalInMetalType; // "Gold" or "Silver"
                bankType = options.metalInMetalPaidFrom || ""; // Bank account for Metal in Metal
                payMode = options.metalInMetalType; // "Gold" or "Silver"
                payWt = options.metalInMetalWeight || ""; // Weight from settlement table
                payAmt = ""; // No amount for Metal in Metal
                // console.log(
                //   `✅ MIM detected: metalType=${metalType}, payMode=${payMode}, payWt=${payWt}, bankType=${bankType}`
                // );
              }
              // Check Metal in Amount (MIA)
              else if (
                options.metalInAmountType &&
                options.metalInAmountWeight
              ) {
                settlementType = "MIA"; // Metal in Amount
                metalType = options.metalInAmountType; // "Gold" or "Silver"
                bankType = options.metalInAmountPaidFrom || "";
                payMode = options.metalInAmountPaymentMode || "";
                payWt = options.metalInAmountWeight || ""; // Weight from settlement table
                payAmt = options.metalInAmountAmount || ""; // Amount from settlement table
                // console.log(
                //   `✅ MIA detected: metalType=${metalType}, payMode=${payMode}, payWt=${payWt}, payAmt=${payAmt}, bankType=${bankType}`
                // );
              }
              // Check Amount in Amount (AIA)
              else if (options.amountInAmountAmount) {
                settlementType = "AIA"; // Amount in Amount
                metalType = ""; // No metal type for Amount in Amount
                bankType = options.amountInAmountPaidFrom || "";
                payMode = options.amountInAmountPaymentMode || "";
                payWt = ""; // No weight for Amount in Amount
                payAmt = options.amountInAmountAmount || ""; // Amount from settlement table
                // console.log(
                //   `✅ AIA detected: payMode=${payMode}, payAmt=${payAmt}, bankType=${bankType}`
                // );
              }
            }
          } else {
            // Default case when no settlement payment is selected
            if (orderData?.isRepairingOrder) {
              settlementType = "AIA";
              metalType = ""; // No metal type for Amount in Amount
              payWt = "";
              payAmt = "";
              // console.log(
              //   `⚠️ No settlement options selected for repairing order, defaulting to AIA`
              // );
            } else {
              settlementType = "MIA";
              metalType = "Gold"; // Default to Gold for MIA
              payWt = "";
              payAmt = "";
              // console.log(
              //   `⚠️ No settlement options selected, defaulting to MIA with Gold`
              // );
            }
          }

          // Build payload based on order type
          let receiveDataPayload;

          if (orderData?.isRepairingOrder) {
            // Repairing order → POST /api/repairing_orders type received_and_update
            // Map UI values to API fields: purity, labor, wastage, note, amount_received, etc.
            const options = settlePaymentOptions[itemId] || {};
            receiveDataPayload = {
              id: parseInt(itemId),
              ornament_weight: ornamentWeight,
              purity: purity,
              labor: laborPercentage,
              wastage: wastagePercentage,
              payable_weight: displayedPayableWeight,
              note: receiveData.note || "",
              received_emp_id: userData?.id || 0,
              gold_received: 0,
              silver_received: 0,
              amount_received: options.amountInAmountAmount
                ? parseFloat(options.amountInAmountAmount)
                : 0,
              bank_type:
                options.amountInAmountPaidFrom &&
                options.amountInAmountPaidFrom !== ""
                  ? parseInt(options.amountInAmountPaidFrom)
                  : null,
              pay_mode: options.amountInAmountPaymentMode || null,
            };
          } else {
            // Custom order → POST /api/custom_orders type receive
            receiveDataPayload = {
              id: parseInt(itemId), // Custom order entry item ID
              ornament_weight: ornamentWeight,
              received_purity: purity, // API expects 'received_purity' not 'purity'
              labor: laborPercentage, // API expects 'labor' not 'labor_percentage'
              payable_weight: displayedPayableWeight, // Use the actual displayed value
              charged_amt: parseFloat(receiveData.charged_amount || 0), // API expects 'charged_amt'
              received_note: receiveData.note || "", // API expects 'received_note'
              received_emp_id: userData?.id || 0,
              received_date: receiveDate,
              type: settlementType, // MIA, MIM, or AIA
              metal_type: metalType, // Gold or Silver
              bank_type: bankType && bankType !== "" ? parseInt(bankType) : "", // Convert to integer if exists and not empty
              pay_mode: payMode,
              pay_wt: payWt ? parseFloat(payWt) : "", // Weight value from settlement table
              pay_amt: payAmt ? parseFloat(payAmt) : "", // Amount value from settlement table
            };
          }

          // console.log(
          //   `🚀 Sending receive data for item ${itemId}:`,
          //   receiveDataPayload
          // );
          // console.log(
          //   `🔍 Final values: type=${settlementType}, metal_type=${metalType}, pay_mode=${payMode}, pay_wt=${payWt}, pay_amt=${payAmt}, bank_type=${bankType}`
          // );

          const idempotency_key =
            typeof crypto !== "undefined" && crypto.randomUUID
              ? crypto.randomUUID()
              : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

          const body = orderData?.isRepairingOrder
            ? await receivedAndUpdateRepairingOrderItem({
                ...receiveDataPayload,
                idempotency_key,
              })
            : await receiveCustomOrderItem({
                ...receiveDataPayload,
                idempotency_key,
              });

          return body;
        }
      );

      // Filter out null promises and execute all receives
      const validPromises = receivePromises.filter(
        (promise) => promise !== null
      );
      const responses = await Promise.all(validPromises);

      // console.log(
      //   "📊 All API responses:",
      //   responses.map((r) => r.data)
      // );

      // Check if all receives were successful
      const successfulResponses = [];
      const failedResponses = [];

      responses.forEach((body) => {
        const ok =
          isErpApiSuccess(body) &&
          body?.success !== false &&
          !body?.error;
        if (ok) {
          successfulResponses.push(body);
        } else {
          failedResponses.push(body);
        }
      });

      if (failedResponses.length === 0) {
        // All successful
        const successMessage = "Items received and updated successfully!";
        toast.success(successMessage);
        onReceiveSuccess && onReceiveSuccess();
      } else if (successfulResponses.length === 0) {
        // All failed
        const errorMessage =
          failedResponses[0]?.message ||
          failedResponses[0]?.error ||
          "All receive operations failed. Please try again.";
        toast.error(errorMessage);
      } else {
        // Mixed results
        toast.warning(
          `${failedResponses.length} operation(s) failed. ${successfulResponses.length} succeeded.`
        );
      }
    } catch (error) {
      console.error("❌ Error receiving items:", error);
      console.error("❌ Error response data:", error.response?.data);

      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        "Failed to receive items";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const fetchProductGroups = async (fetchedKarigars = null) => {
    try {
      // Use passed karigars or current state
      const karigarsList = fetchedKarigars || karigars;

      // Extract product groups from orderData.custom_order_entry
      if (
        orderData &&
        orderData.custom_order_entry &&
        Array.isArray(orderData.custom_order_entry)
      ) {
        const groups = orderData.custom_order_entry.map((item) => {
          // Find the assigned karigar from the karigars array
          const assignedKarigar = karigarsList.find(
            (k) => k.id.toString() === item.karigar_id?.toString()
          );

          return {
            id: item.id,
            order_number: item.order_number,
            product_group: item.product_group,
            design: item.design,
            size: item.size,
            pcs: item.pcs,
            gwt: item.gwt,
            lwt: item.lwt,
            nwt: item.nwt,
            totalamt: item.totalamt,
            status: item.status,
            // Use karigar_id from the API response
            assigned_karigar_id: item.karigar_id || null,
            assigned_karigar_name: assignedKarigar?.fullName || null,
            assigned_karigar_company: assignedKarigar?.companyName || null,
          };
        });
        setProductGroups(groups);

        // Initialize receive data for each item
        const initialReceiveData = {};
        const initialSettlePaymentOpen = {};
        const initialSettlePaymentOptions = {};
        groups.forEach((item) => {
          initialReceiveData[item.id] = {
            ornament_weight: "",
            purity: "",
            labor_percentage: "",
            wastage_percentage: "",
            charged_amount: "",
            note: "",
          };

          initialSettlePaymentOpen[item.id] = false;
          initialSettlePaymentOptions[item.id] = {
            metalInMetal: false,
            metalInAmount: false,
            amountInAmount: false,
            // Initialize Amount in Amount fields for repairing orders
            ...(orderData?.isRepairingOrder && {
              amountInAmountAmount: "",
              amountInAmountPaymentMode: "",
              amountInAmountPaidFrom: "",
            }),
            // Initialize Metal in Amount fields for non-repairing orders
            ...(!orderData?.isRepairingOrder && {
              metalInAmountType: "Gold",
              metalInAmountWeight: "",
              metalInAmountAmount: "",
              metalInAmountPaymentMode: "",
              metalInAmountPaidFrom: "",
            }),
          };
        });
        setItemReceiveData(initialReceiveData);
        setSettlePaymentOpen(initialSettlePaymentOpen);
        setSettlePaymentOptions(initialSettlePaymentOptions);

        // Fetch product group names
        const response = await listProductGroups();
        if (response?.success && Array.isArray(response.data?.product_groups)) {
          const names = {};
          response.data.product_groups.forEach((group) => {
            names[group.id] = group.name || "";
          });
          setProductGroupNames(names);
        }
      }
    } catch (error) {
      console.error("Error processing product groups:", error);
      toast.error("Failed to load product groups");
    }
  };

  const handleItemFieldChange = (itemId, field, value) => {
    setItemReceiveData((prev) => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        [field]: value,
      },
    }));
  };

  const toggleSettlePayment = (itemId) => {
    setSettlePaymentOpen((prev) => {
      const isOpening = !prev[itemId];

      // Initialize default values when opening the settle payment section
      if (isOpening) {
        setSettlePaymentOptions((prevOptions) => ({
          ...prevOptions,
          [itemId]: {
            ...prevOptions[itemId],
            // Set default values based on order type
            ...(orderData?.isRepairingOrder
              ? {
                  // For repairing orders, set default values for Amount in Amount
                  amountInAmountAmount: "",
                  amountInAmountPaymentMode: "",
                  amountInAmountPaidFrom: "",
                }
              : {
                  // For non-repairing orders, set default values for Metal in Amount
                  metalInAmountType: "Gold", // Default to Gold
                }),
          },
        }));
      }

      return {
        ...prev,
        [itemId]: !prev[itemId],
      };
    });
  };

  const handleSettlePaymentOptionChange = (itemId, option, value) => {
    setSettlePaymentOptions((prev) => {
      const updatedOptions = {
        ...prev,
        [itemId]: {
          ...prev[itemId],
          [option]: value,
        },
      };

      // Auto-select payment mode and bank for Metal in Metal
      if (option === "metalInMetalType" && value) {
        // Auto-select payment mode (Gold -> Gold, Silver -> Silver)
        updatedOptions[itemId].metalInMetalPaymentMode = value;

        // Auto-select bank account based on metal type
        let targetBank = null;

        if (value === "Gold") {
          // First try to use the selected gold bank account
          if (goldBankAccount) {
            targetBank = goldBankAccount;
          } else {
            // Fallback: find a bank with "Gold" in the name
            targetBank = bankAccounts.find((bank) =>
              bank.bankName.toLowerCase().includes("gold")
            );
          }
        } else if (value === "Silver") {
          // First try to use the selected silver bank account
          if (silverBankAccount) {
            targetBank = silverBankAccount;
          } else {
            // Fallback: find a bank with "Silver" in the name
            targetBank = bankAccounts.find((bank) =>
              bank.bankName.toLowerCase().includes("silver")
            );
          }
        }

        // Set the bank account if found
        if (targetBank) {
          updatedOptions[itemId].metalInMetalPaidFrom = targetBank.id;
        }
      }

      // Auto-calculate amount for Metal in Amount based on weight and live rates
      if (option === "metalInAmountWeight") {
        const weight = parseFloat(value) || 0;
        const metalType = prev[itemId]?.metalInAmountType;

        if (weight > 0 && metalType) {
          if (metalType === "Gold") {
            // Calculate gold amount based on live rates
            const perGramRate = calculatePerGramRate(masterGoldRate);
            const calculatedAmount = (weight * perGramRate).toFixed(2);
            updatedOptions[itemId].metalInAmountAmount = calculatedAmount;
          } else if (metalType === "Silver") {
            // Calculate silver amount based on live rates
            const perGramRate = calculatePerGramSilverRate(masterSilverRate);
            const calculatedAmount = (weight * perGramRate).toFixed(2);
            updatedOptions[itemId].metalInAmountAmount = calculatedAmount;
          }
        } else {
          if (weight <= 0) {
            updatedOptions[itemId].metalInAmountAmount = "";
          } else if (!metalType) {
            // console.log("No metal type selected - not calculating");
          }
        }
      }

      // Also update the amount when the metal type changes for Metal in Amount
      if (option === "metalInAmountType" && prev[itemId]?.metalInAmountWeight) {
        const weight = parseFloat(prev[itemId]?.metalInAmountWeight) || 0;
        const metalType = value;

        if (weight > 0) {
          if (metalType === "Gold") {
            const perGramRate = calculatePerGramRate(masterGoldRate);
            const calculatedAmount = (weight * perGramRate).toFixed(2);
            updatedOptions[itemId].metalInAmountAmount = calculatedAmount;
          } else if (metalType === "Silver") {
            const perGramRate = calculatePerGramSilverRate(masterSilverRate);
            const calculatedAmount = (weight * perGramRate).toFixed(2);
            updatedOptions[itemId].metalInAmountAmount = calculatedAmount;
          }
        }
      }

      return updatedOptions;
    });
  };

  return (
    <Modal show={show} onHide={onHide} centered size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Receive & Update Items</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form>
          {/* Receive Date */}
          <Row className="mb-3">
            <Col md={2}>
              <Form.Label>Receive Date</Form.Label>
              <Form.Control
                {...getReceiveDateInputProps(receiveDate, (e) =>
                  setReceiveDate(e.target.value),
                )}
              />
            </Col>
          </Row>

          {/* Product Groups and Item Receive Data */}
          {productGroups.length > 0 && (
            <div className="mb-4">
              {productGroups.map((item, index) => (
                <div key={item.id} className="mb-4 border rounded p-3">
                  <div className="mb-3">
                    <h6 className="mb-1 d-flex align-items-center gap-2 flex-wrap">
                      <span>
                        Item {index + 1}:{" "}
                        {productGroupNames[item.product_group] ||
                          `Product Group ID: ${item.product_group}`}
                      </span>
                      {item.assigned_karigar_name && (
                        <>
                          <span className={`badge ${statusColors.teal}`}>
                            {item.assigned_karigar_name}
                            {item.assigned_karigar_company &&
                              ` (${item.assigned_karigar_company})`}
                          </span>
                          {(() => {
                            const assignedKarigar = karigars.find(
                              (k) =>
                                k.id.toString() ===
                                item.assigned_karigar_id?.toString()
                            );
                            return assignedKarigar ? (
                              <>
                                <span
                                  className={`badge ${
                                    parseFloat(assignedKarigar.gold) > 0
                                      ? statusColors.success
                                      : parseFloat(assignedKarigar.gold) < 0
                                      ? statusColors.danger
                                      : "bg-light text-dark"
                                  }`}
                                  style={{ fontSize: "0.85rem" }}
                                >
                                  Gold:{" "}
                                  {parseFloat(
                                    assignedKarigar.gold || 0
                                  ).toFixed(2)}{" "}
                                  gm
                                </span>
                                <span
                                  className={`badge ${
                                    parseFloat(assignedKarigar.silver) > 0
                                      ? statusColors.success
                                      : parseFloat(assignedKarigar.silver) < 0
                                      ? statusColors.danger
                                      : "bg-light text-dark"
                                  }`}
                                  style={{ fontSize: "0.85rem" }}
                                >
                                  Silver:{" "}
                                  {parseFloat(
                                    assignedKarigar.silver || 0
                                  ).toFixed(2)}{" "}
                                  gm
                                </span>
                                <span
                                  className={`badge ${
                                    parseFloat(assignedKarigar.balance) > 0
                                      ? statusColors.success
                                      : parseFloat(assignedKarigar.balance) < 0
                                      ? statusColors.danger
                                      : "bg-light text-dark"
                                  }`}
                                  style={{ fontSize: "0.85rem" }}
                                >
                                  Balance: ₹
                                  {parseFloat(
                                    assignedKarigar.balance || 0
                                  ).toFixed(2)}
                                </span>
                              </>
                            ) : null;
                          })()}
                        </>
                      )}
                    </h6>
                    {(parseFloat(item.gwt || 0) > 0 ||
                      parseFloat(item.nwt || 0) > 0) && (
                      <div className="mt-1">
                        <span
                          className="badge bg-info-subtle text-info"
                          style={{ fontSize: "0.75rem" }}
                        >
                          GWT: {parseFloat(item.gwt || 0).toFixed(2)} gm
                          {parseFloat(item.nwt || 0) > 0 && (
                            <>
                              {" "}
                              | NWT: {parseFloat(item.nwt || 0).toFixed(2)} gm
                            </>
                          )}
                        </span>
                      </div>
                    )}
                  </div>

                  <Row className="mb-3">
                    <Col md={2}>
                      <Form.Label>Ornament Weight</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="Enter Weight"
                        value={itemReceiveData[item.id]?.ornament_weight || ""}
                        onChange={(e) =>
                          handleItemFieldChange(
                            item.id,
                            "ornament_weight",
                            e.target.value
                          )
                        }
                      />
                    </Col>
                    <Col md={2}>
                      <Form.Label>
                        Purity (%)
                        {itemReceiveData[item.id]?.ornament_weight &&
                          itemReceiveData[item.id]?.purity && (
                            <span className="text-secondary ms-2">
                              {(
                                (parseFloat(
                                  itemReceiveData[item.id]?.ornament_weight || 0
                                ) *
                                  parseFloat(
                                    itemReceiveData[item.id]?.purity || 0
                                  )) /
                                100
                              ).toFixed(2)}
                              gm
                            </span>
                          )}
                      </Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        placeholder="Enter Purity"
                        value={itemReceiveData[item.id]?.purity || ""}
                        onChange={(e) =>
                          handleItemFieldChange(
                            item.id,
                            "purity",
                            e.target.value
                          )
                        }
                      />
                    </Col>
                    <Col md={2}>
                      <Form.Label>
                        Labor (%)
                        {itemReceiveData[item.id]?.ornament_weight &&
                          itemReceiveData[item.id]?.purity &&
                          itemReceiveData[item.id]?.labor_percentage && (
                            <span className="text-secondary ms-2">
                              {(
                                ((parseFloat(
                                  itemReceiveData[item.id]?.ornament_weight || 0
                                ) *
                                  parseFloat(
                                    itemReceiveData[item.id]?.purity || 0
                                  )) /
                                  100) *
                                (parseFloat(
                                  itemReceiveData[item.id]?.labor_percentage ||
                                    0
                                ) /
                                  100)
                              ).toFixed(2)}
                              gm
                            </span>
                          )}
                      </Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="Enter Labor"
                        value={itemReceiveData[item.id]?.labor_percentage || ""}
                        onChange={(e) =>
                          handleItemFieldChange(
                            item.id,
                            "labor_percentage",
                            e.target.value
                          )
                        }
                      />
                    </Col>
                    <Col md={2}>
                      <Form.Label>
                        Wastage (%)
                        {itemReceiveData[item.id]?.ornament_weight &&
                          itemReceiveData[item.id]?.purity &&
                          itemReceiveData[item.id]?.wastage_percentage && (
                            <span className="text-secondary ms-2">
                              {(
                                ((parseFloat(
                                  itemReceiveData[item.id]?.ornament_weight || 0
                                ) *
                                  parseFloat(
                                    itemReceiveData[item.id]?.purity || 0
                                  )) /
                                  100) *
                                (parseFloat(
                                  itemReceiveData[item.id]
                                    ?.wastage_percentage || 0
                                ) /
                                  100)
                              ).toFixed(2)}
                              gm
                            </span>
                          )}
                      </Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="Enter Wastage"
                        value={
                          itemReceiveData[item.id]?.wastage_percentage || ""
                        }
                        onChange={(e) =>
                          handleItemFieldChange(
                            item.id,
                            "wastage_percentage",
                            e.target.value
                          )
                        }
                      />
                    </Col>
                    <Col md={2}>
                      <Form.Label>Payable Weight</Form.Label>
                      <Form.Control
                        type="text"
                        value={
                          itemReceiveData[item.id]?.ornament_weight &&
                          itemReceiveData[item.id]?.purity &&
                          (itemReceiveData[item.id]?.labor_percentage ||
                            itemReceiveData[item.id]?.wastage_percentage)
                            ? // Calculate 24k weight first
                              (
                                ((parseFloat(
                                  itemReceiveData[item.id]?.ornament_weight || 0
                                ) *
                                  parseFloat(
                                    itemReceiveData[item.id]?.purity || 0
                                  )) /
                                  100) *
                                // Then add labor and wastage percentages to the 24k weight
                                (1 +
                                  (parseFloat(
                                    itemReceiveData[item.id]
                                      ?.labor_percentage || 0
                                  ) +
                                    parseFloat(
                                      itemReceiveData[item.id]
                                        ?.wastage_percentage || 0
                                    )) /
                                    100)
                              ).toFixed(2)
                            : itemReceiveData[item.id]?.ornament_weight &&
                              itemReceiveData[item.id]?.purity
                            ? (
                                (parseFloat(
                                  itemReceiveData[item.id]?.ornament_weight || 0
                                ) *
                                  parseFloat(
                                    itemReceiveData[item.id]?.purity || 0
                                  )) /
                                100
                              ).toFixed(3)
                            : "0"
                        }
                        readOnly
                        className="bg-light"
                      />
                    </Col>
                    <Col md={2}>
                      <Form.Label>Charged Amount</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="Enter Amount"
                        value={itemReceiveData[item.id]?.charged_amount || ""}
                        onChange={(e) =>
                          handleItemFieldChange(
                            item.id,
                            "charged_amount",
                            e.target.value
                          )
                        }
                      />
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={12}>
                      <Form.Label>Note</Form.Label>
                      <Form.Control
                        placeholder="Enter any notes for this receive..."
                        value={itemReceiveData[item.id]?.note || ""}
                        onChange={(e) =>
                          handleItemFieldChange(item.id, "note", e.target.value)
                        }
                      />
                    </Col>
                  </Row>

                  {/* Settle Payment Checkbox and Table */}
                  <Row className="mb-3">
                    <Col xs={12}>
                      <div className="d-flex flex-column flex-xl-row align-items-start gap-3">
                        {/* Settle Payment Checkbox - Fixed Position */}
                        <div
                          className="d-flex align-items-center mb-2 mb-xl-0 flex-shrink-0"
                          style={{
                            minWidth: "140px",
                            height: "40px",
                            marginTop: "0px",
                          }}
                        >
                          <Form.Check
                            type="checkbox"
                            id={`settle-payment-${item.id}`}
                            label="Settle Payment"
                            checked={settlePaymentOpen[item.id] || false}
                            onChange={() => toggleSettlePayment(item.id)}
                          />
                        </div>

                        {/* Table Container */}
                        <div className="flex-grow-1 w-100 overflow-auto">
                          <Collapse in={settlePaymentOpen[item.id] || false}>
                            <div>
                              <div
                                className="table-responsive border rounded-1"
                                style={{ maxWidth: "100%", overflowX: "auto" }}
                              >
                                <table
                                  className="table text-nowrap mb-0 align-middle table-bordered theme-header-table"
                                  style={{
                                    minWidth: "800px",
                                    fontSize: "0.8rem",
                                  }}
                                >
                                  <thead className="text-dark fs-4">
                                    <tr>
                                      <th
                                        className="px-1 py-2 text-center"
                                        style={{
                                          fontSize: "0.85rem",
                                          minWidth: "90px",
                                          fontWeight: "600",
                                          whiteSpace: "nowrap",
                                          border: "1px solid #dee2e6",
                                        }}
                                      >
                                        Tnx Type
                                      </th>
                                      <th
                                        className="px-1 py-2 text-center"
                                        style={{
                                          fontSize: "0.85rem",
                                          minWidth: "80px",
                                          fontWeight: "600",
                                          whiteSpace: "nowrap",
                                          border: "1px solid #dee2e6",
                                        }}
                                      >
                                        Metal Type
                                      </th>
                                      <th
                                        className="px-1 py-2 text-center"
                                        style={{
                                          fontSize: "0.85rem",
                                          minWidth: "60px",
                                          fontWeight: "600",
                                          whiteSpace: "nowrap",
                                          border: "1px solid #dee2e6",
                                        }}
                                      >
                                        Wt
                                      </th>
                                      <th
                                        className="px-1 py-2 text-center"
                                        style={{
                                          fontSize: "0.85rem",
                                          minWidth: "70px",
                                          fontWeight: "600",
                                          whiteSpace: "nowrap",
                                          border: "1px solid #dee2e6",
                                        }}
                                      >
                                        Amt
                                      </th>
                                      <th
                                        className="px-1 py-2 text-center"
                                        style={{
                                          fontSize: "0.85rem",
                                          minWidth: "100px",
                                          fontWeight: "600",
                                          whiteSpace: "nowrap",
                                          border: "1px solid #dee2e6",
                                        }}
                                      >
                                        Pay Mode
                                      </th>
                                      <th
                                        className="px-1 py-2 text-center"
                                        style={{
                                          fontSize: "0.85rem",
                                          minWidth: "110px",
                                          fontWeight: "600",
                                          whiteSpace: "nowrap",
                                          border: "1px solid #dee2e6",
                                        }}
                                      >
                                        Paid From
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {/* Only show Metal in Metal and Metal in Amount rows for non-repairing orders */}
                                    {!orderData?.isRepairingOrder && (
                                      <>
                                        {/* Metal in Metal Row */}
                                        <tr
                                          style={{ backgroundColor: "#ffffff" }}
                                        >
                                          <td
                                            className="p-0"
                                            style={{
                                              border: "1px solid #dee2e6",
                                            }}
                                          >
                                            <div
                                              className="d-flex align-items-center"
                                              style={{
                                                height: "36px",
                                                paddingLeft: "8px",
                                              }}
                                            >
                                              <span
                                                style={{
                                                  fontSize: "0.85rem",
                                                  fontWeight: "500",
                                                }}
                                              >
                                                Metal in Metal
                                              </span>
                                            </div>
                                          </td>
                                          <td
                                            className="p-0"
                                            style={{
                                              border: "1px solid #dee2e6",
                                            }}
                                          >
                                            <div
                                              style={{
                                                height: "38px",
                                                display: "flex",
                                                alignItems: "center",
                                              }}
                                            >
                                              <select
                                                className="form-select form-select-sm border-0"
                                                value={
                                                  settlePaymentOptions[item.id]
                                                    ?.metalInMetalType || ""
                                                }
                                                onChange={(e) =>
                                                  handleSettlePaymentOptionChange(
                                                    item.id,
                                                    "metalInMetalType",
                                                    e.target.value
                                                  )
                                                }
                                                style={{ fontSize: "0.85rem" }}
                                              >
                                                <option value="">
                                                  Select Type
                                                </option>
                                                <option value="Gold">
                                                  Gold
                                                </option>
                                                <option value="Silver">
                                                  Silver
                                                </option>
                                              </select>
                                            </div>
                                          </td>
                                          <td
                                            className="p-0"
                                            style={{
                                              border: "1px solid #dee2e6",
                                            }}
                                          >
                                            <div
                                              style={{
                                                height: "38px",
                                                display: "flex",
                                                alignItems: "center",
                                              }}
                                            >
                                              <input
                                                type="number"
                                                step="0.01"
                                                className="form-control form-control-sm border-0"
                                                value={
                                                  settlePaymentOptions[item.id]
                                                    ?.metalInMetalWeight || ""
                                                }
                                                onChange={(e) =>
                                                  handleSettlePaymentOptionChange(
                                                    item.id,
                                                    "metalInMetalWeight",
                                                    e.target.value
                                                  )
                                                }
                                                style={{ fontSize: "0.85rem" }}
                                              />
                                            </div>
                                          </td>
                                          <td
                                            className="p-0"
                                            style={{
                                              border: "1px solid #dee2e6",
                                            }}
                                          >
                                            <div
                                              className="d-flex align-items-center justify-content-center"
                                              style={{
                                                height: "38px",
                                                backgroundColor: "#f8f9fa",
                                                cursor: "not-allowed",
                                                position: "relative",
                                              }}
                                              onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor =
                                                  "#e9ecef";
                                                const crossIcon =
                                                  e.currentTarget.querySelector(
                                                    ".cross-icon"
                                                  );
                                                if (crossIcon)
                                                  crossIcon.style.display =
                                                    "inline";
                                              }}
                                              onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor =
                                                  "#f8f9fa";
                                                const crossIcon =
                                                  e.currentTarget.querySelector(
                                                    ".cross-icon"
                                                  );
                                                if (crossIcon)
                                                  crossIcon.style.display =
                                                    "none";
                                              }}
                                            >
                                              <span
                                                style={{
                                                  fontSize: "0.85rem",
                                                  color: "#6c757d",
                                                }}
                                              >
                                                -
                                              </span>
                                            </div>
                                          </td>
                                          <td
                                            className="p-0"
                                            style={{
                                              border: "1px solid #dee2e6",
                                            }}
                                          >
                                            <div
                                              style={{
                                                height: "38px",
                                                display: "flex",
                                                alignItems: "center",
                                              }}
                                            >
                                              <select
                                                className="form-select form-select-sm border-0"
                                                value={
                                                  settlePaymentOptions[item.id]
                                                    ?.metalInMetalPaymentMode ||
                                                  ""
                                                }
                                                onChange={(e) =>
                                                  handleSettlePaymentOptionChange(
                                                    item.id,
                                                    "metalInMetalPaymentMode",
                                                    e.target.value
                                                  )
                                                }
                                                style={{ fontSize: "0.85rem" }}
                                              >
                                                <option value="">
                                                  Select Mode
                                                </option>
                                                <option value="Gold">
                                                  Gold
                                                </option>
                                                <option value="Silver">
                                                  Silver
                                                </option>
                                              </select>
                                            </div>
                                          </td>
                                          <td
                                            className="p-0"
                                            style={{
                                              border: "1px solid #dee2e6",
                                            }}
                                          >
                                            <div
                                              style={{
                                                height: "38px",
                                                display: "flex",
                                                alignItems: "center",
                                              }}
                                            >
                                              <select
                                                className="form-select form-select-sm border-0"
                                                value={
                                                  settlePaymentOptions[item.id]
                                                    ?.metalInMetalPaidFrom || ""
                                                }
                                                onChange={(e) =>
                                                  handleSettlePaymentOptionChange(
                                                    item.id,
                                                    "metalInMetalPaidFrom",
                                                    e.target.value
                                                  )
                                                }
                                                style={{ fontSize: "0.85rem" }}
                                              >
                                                <option value="">
                                                  Select Account
                                                </option>
                                                {bankAccounts.map((bank) => (
                                                  <option
                                                    key={bank.id}
                                                    value={bank.id}
                                                  >
                                                    {bank.bankName}
                                                  </option>
                                                ))}
                                              </select>
                                            </div>
                                          </td>
                                        </tr>

                                        {/* Metal in Amount Row */}
                                        <tr
                                          style={{ backgroundColor: "#ffffff" }}
                                        >
                                          <td
                                            className="p-0"
                                            style={{
                                              border: "1px solid #dee2e6",
                                            }}
                                          >
                                            <div
                                              className="d-flex align-items-center"
                                              style={{
                                                height: "36px",
                                                paddingLeft: "8px",
                                              }}
                                            >
                                              <span
                                                style={{
                                                  fontSize: "0.85rem",
                                                  fontWeight: "500",
                                                }}
                                              >
                                                Metal in Amount
                                              </span>
                                            </div>
                                          </td>
                                          <td
                                            className="p-0"
                                            style={{
                                              border: "1px solid #dee2e6",
                                            }}
                                          >
                                            <div
                                              style={{
                                                height: "38px",
                                                display: "flex",
                                                alignItems: "center",
                                              }}
                                            >
                                              <select
                                                className="form-select form-select-sm border-0"
                                                value={
                                                  settlePaymentOptions[item.id]
                                                    ?.metalInAmountType || ""
                                                }
                                                onChange={(e) =>
                                                  handleSettlePaymentOptionChange(
                                                    item.id,
                                                    "metalInAmountType",
                                                    e.target.value
                                                  )
                                                }
                                                style={{ fontSize: "0.85rem" }}
                                              >
                                                <option value="">
                                                  Select Type
                                                </option>
                                                <option value="Gold">
                                                  Gold
                                                </option>
                                                <option value="Silver">
                                                  Silver
                                                </option>
                                              </select>
                                            </div>
                                          </td>
                                          <td
                                            className="p-0"
                                            style={{
                                              border: "1px solid #dee2e6",
                                            }}
                                          >
                                            <div
                                              style={{
                                                height: "38px",
                                                display: "flex",
                                                alignItems: "center",
                                              }}
                                            >
                                              <input
                                                type="number"
                                                step="0.01"
                                                className="form-control form-control-sm border-0"
                                                value={
                                                  settlePaymentOptions[item.id]
                                                    ?.metalInAmountWeight || ""
                                                }
                                                onChange={(e) =>
                                                  handleSettlePaymentOptionChange(
                                                    item.id,
                                                    "metalInAmountWeight",
                                                    e.target.value
                                                  )
                                                }
                                                style={{ fontSize: "0.85rem" }}
                                              />
                                            </div>
                                          </td>
                                          <td
                                            className="p-0"
                                            style={{
                                              border: "1px solid #dee2e6",
                                            }}
                                          >
                                            <div
                                              style={{
                                                height: "38px",
                                                display: "flex",
                                                alignItems: "center",
                                              }}
                                            >
                                              <div
                                                style={{
                                                  position: "relative",
                                                  width: "100%",
                                                }}
                                              >
                                                <input
                                                  type="number"
                                                  step="0.01"
                                                  className="form-control form-control-sm border-0"
                                                  value={
                                                    settlePaymentOptions[
                                                      item.id
                                                    ]?.metalInAmountAmount || ""
                                                  }
                                                  onChange={(e) =>
                                                    handleSettlePaymentOptionChange(
                                                      item.id,
                                                      "metalInAmountAmount",
                                                      e.target.value
                                                    )
                                                  }
                                                  style={{
                                                    fontSize: "0.85rem",
                                                  }}
                                                />
                                                {settlePaymentOptions[item.id]
                                                  ?.metalInAmountType ===
                                                  "Gold" && (
                                                  <span
                                                    style={{
                                                      position: "absolute",
                                                      right: "10px",
                                                      top: "50%",
                                                      transform:
                                                        "translateY(-50%)",
                                                      fontSize: "0.65rem",
                                                      color: "#6c757d",
                                                    }}
                                                  >
                                                    @
                                                    {calculatePerGramRate(
                                                      masterGoldRate
                                                    ).toFixed(2)}
                                                    /g
                                                  </span>
                                                )}
                                                {settlePaymentOptions[item.id]
                                                  ?.metalInAmountType ===
                                                  "Silver" && (
                                                  <span
                                                    style={{
                                                      position: "absolute",
                                                      right: "10px",
                                                      top: "50%",
                                                      transform:
                                                        "translateY(-50%)",
                                                      fontSize: "0.65rem",
                                                      color: "#6c757d",
                                                    }}
                                                  >
                                                    @
                                                    {calculatePerGramSilverRate(
                                                      masterSilverRate
                                                    ).toFixed(2)}
                                                    /g
                                                  </span>
                                                )}
                                              </div>
                                            </div>
                                          </td>
                                          <td
                                            className="p-0"
                                            style={{
                                              border: "1px solid #dee2e6",
                                            }}
                                          >
                                            <div
                                              style={{
                                                height: "38px",
                                                display: "flex",
                                                alignItems: "center",
                                              }}
                                            >
                                              <select
                                                className="form-select form-select-sm border-0"
                                                value={
                                                  settlePaymentOptions[item.id]
                                                    ?.metalInAmountPaymentMode ||
                                                  ""
                                                }
                                                onChange={(e) =>
                                                  handleSettlePaymentOptionChange(
                                                    item.id,
                                                    "metalInAmountPaymentMode",
                                                    e.target.value
                                                  )
                                                }
                                                style={{ fontSize: "0.85rem" }}
                                              >
                                                <option value="">
                                                  Select Mode
                                                </option>
                                                <option value="Cash">
                                                  Cash
                                                </option>
                                                <option value="UPI">UPI</option>
                                                <option value="Bank Transfer">
                                                  Bank Transfer
                                                </option>
                                                <option value="Cheque">
                                                  Cheque
                                                </option>
                                                <option value="Card">
                                                  Card
                                                </option>
                                              </select>
                                            </div>
                                          </td>
                                          <td
                                            className="p-0"
                                            style={{
                                              border: "1px solid #dee2e6",
                                            }}
                                          >
                                            <div
                                              style={{
                                                height: "38px",
                                                display: "flex",
                                                alignItems: "center",
                                              }}
                                            >
                                              <select
                                                className="form-select form-select-sm border-0"
                                                value={
                                                  settlePaymentOptions[item.id]
                                                    ?.metalInAmountPaidFrom ||
                                                  ""
                                                }
                                                onChange={(e) =>
                                                  handleSettlePaymentOptionChange(
                                                    item.id,
                                                    "metalInAmountPaidFrom",
                                                    e.target.value
                                                  )
                                                }
                                                style={{ fontSize: "0.85rem" }}
                                              >
                                                <option value="">
                                                  Select Account
                                                </option>
                                                {bankAccounts.map((bank) => (
                                                  <option
                                                    key={bank.id}
                                                    value={bank.id}
                                                  >
                                                    {bank.bankName}
                                                  </option>
                                                ))}
                                              </select>
                                            </div>
                                          </td>
                                        </tr>
                                      </>
                                    )}

                                    {/* Amount in Amount Row */}
                                    <tr style={{ backgroundColor: "#ffffff" }}>
                                      <td
                                        className="p-0"
                                        style={{ border: "1px solid #dee2e6" }}
                                      >
                                        <div
                                          className="d-flex align-items-center"
                                          style={{
                                            height: "36px",
                                            paddingLeft: "8px",
                                          }}
                                        >
                                          <span
                                            style={{
                                              fontSize: "0.85rem",
                                              fontWeight: "500",
                                            }}
                                          >
                                            Amount in Amount
                                          </span>
                                        </div>
                                      </td>
                                      <td
                                        className="p-0"
                                        style={{ border: "1px solid #dee2e6" }}
                                      >
                                        <div
                                          className="d-flex align-items-center justify-content-center"
                                          style={{
                                            height: "38px",
                                            backgroundColor: "#f8f9fa",
                                            cursor: "not-allowed",
                                            position: "relative",
                                          }}
                                          onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor =
                                              "#e9ecef";
                                            const crossIcon =
                                              e.currentTarget.querySelector(
                                                ".cross-icon"
                                              );
                                            if (crossIcon)
                                              crossIcon.style.display =
                                                "inline";
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor =
                                              "#f8f9fa";
                                            const crossIcon =
                                              e.currentTarget.querySelector(
                                                ".cross-icon"
                                              );
                                            if (crossIcon)
                                              crossIcon.style.display = "none";
                                          }}
                                        >
                                          <span
                                            style={{
                                              fontSize: "0.85rem",
                                              color: "#6c757d",
                                            }}
                                          >
                                            -
                                          </span>
                                        </div>
                                      </td>
                                      <td
                                        className="p-0"
                                        style={{ border: "1px solid #dee2e6" }}
                                      >
                                        <div
                                          className="d-flex align-items-center justify-content-center"
                                          style={{
                                            height: "38px",
                                            backgroundColor: "#f8f9fa",
                                            cursor: "not-allowed",
                                            position: "relative",
                                          }}
                                          onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor =
                                              "#e9ecef";
                                            const crossIcon =
                                              e.currentTarget.querySelector(
                                                ".cross-icon"
                                              );
                                            if (crossIcon)
                                              crossIcon.style.display =
                                                "inline";
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor =
                                              "#f8f9fa";
                                            const crossIcon =
                                              e.currentTarget.querySelector(
                                                ".cross-icon"
                                              );
                                            if (crossIcon)
                                              crossIcon.style.display = "none";
                                          }}
                                        >
                                          <span
                                            style={{
                                              fontSize: "0.85rem",
                                              color: "#6c757d",
                                            }}
                                          >
                                            -
                                          </span>
                                        </div>
                                      </td>
                                      <td
                                        className="p-0"
                                        style={{ border: "1px solid #dee2e6" }}
                                      >
                                        <div
                                          style={{
                                            height: "38px",
                                            display: "flex",
                                            alignItems: "center",
                                          }}
                                        >
                                          <input
                                            type="number"
                                            step="0.01"
                                            className="form-control form-control-sm border-0"
                                            value={
                                              settlePaymentOptions[item.id]
                                                ?.amountInAmountAmount || ""
                                            }
                                            onChange={(e) =>
                                              handleSettlePaymentOptionChange(
                                                item.id,
                                                "amountInAmountAmount",
                                                e.target.value
                                              )
                                            }
                                            style={{ fontSize: "0.85rem" }}
                                          />
                                        </div>
                                      </td>
                                      <td
                                        className="p-0"
                                        style={{ border: "1px solid #dee2e6" }}
                                      >
                                        <div
                                          style={{
                                            height: "38px",
                                            display: "flex",
                                            alignItems: "center",
                                          }}
                                        >
                                          <select
                                            className="form-select form-select-sm border-0"
                                            value={
                                              settlePaymentOptions[item.id]
                                                ?.amountInAmountPaymentMode ||
                                              ""
                                            }
                                            onChange={(e) =>
                                              handleSettlePaymentOptionChange(
                                                item.id,
                                                "amountInAmountPaymentMode",
                                                e.target.value
                                              )
                                            }
                                            style={{ fontSize: "0.85rem" }}
                                          >
                                            <option value="">
                                              Select Mode
                                            </option>
                                            <option value="Cash">Cash</option>
                                            <option value="UPI">UPI</option>
                                            <option value="Bank Transfer">
                                              Bank Transfer
                                            </option>
                                            <option value="Cheque">
                                              Cheque
                                            </option>
                                            <option value="Card">Card</option>
                                          </select>
                                        </div>
                                      </td>
                                      <td
                                        className="p-0"
                                        style={{ border: "1px solid #dee2e6" }}
                                      >
                                        <div
                                          style={{
                                            height: "38px",
                                            display: "flex",
                                            alignItems: "center",
                                          }}
                                        >
                                          <select
                                            className="form-select form-select-sm border-0"
                                            value={
                                              settlePaymentOptions[item.id]
                                                ?.amountInAmountPaidFrom || ""
                                            }
                                            onChange={(e) =>
                                              handleSettlePaymentOptionChange(
                                                item.id,
                                                "amountInAmountPaidFrom",
                                                e.target.value
                                              )
                                            }
                                            style={{ fontSize: "0.85rem" }}
                                          >
                                            <option value="">
                                              Select Account
                                            </option>
                                            {bankAccounts.map((bank) => (
                                              <option
                                                key={bank.id}
                                                value={bank.id}
                                              >
                                                {bank.bankName}
                                              </option>
                                            ))}
                                          </select>
                                        </div>
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </Collapse>
                        </div>
                      </div>
                    </Col>
                  </Row>

                  {/* Hidden Gold Payment Mode and Bank Selection */}
                  {parseFloat(
                    itemReceiveData[item.id]?.gold_received || 0
                  ) > 0 && (
                    <Row className="mb-2" style={{ display: "none" }}>
                      <Col md={2}>
                        <Form.Label>Payment Mode</Form.Label>
                        <Form.Control
                          type="text"
                          value="Gold"
                          readOnly
                          className="bg-light"
                        />
                      </Col>

                      <Col md={2}>
                        <Form.Label>Received To</Form.Label>
                        <Form.Select
                          value={goldBankAccount?.id || ""}
                          onChange={(e) => {
                            const selectedBank = bankAccounts.find(
                              (bank) => bank.id.toString() === e.target.value
                            );
                            setGoldBankAccount(selectedBank || null);
                          }}
                        >
                          <option value="">Select Bank</option>
                          {bankAccounts.map((bank) => (
                            <option
                              key={bank.id}
                              value={bank.id}
                              selected={goldBankAccount?.id === bank.id}
                            >
                              {bank.bankName}{" "}
                              {bank.accountNumber
                                ? `- ${bank.accountNumber}`
                                : ""}
                            </option>
                          ))}
                        </Form.Select>
                      </Col>
                    </Row>
                  )}

                  {/* Hidden Silver Payment Mode and Bank Selection */}
                  {parseFloat(
                    itemReceiveData[item.id]?.silver_received || 0
                  ) > 0 && (
                    <Row className="mb-2" style={{ display: "none" }}>
                      <Col md={2}>
                        <Form.Label>Payment Mode</Form.Label>
                        <Form.Control
                          type="text"
                          value="Silver"
                          readOnly
                          className="bg-light"
                        />
                      </Col>

                      <Col md={2}>
                        <Form.Label>Received To</Form.Label>
                        <Form.Select
                          value={silverBankAccount?.id || ""}
                          onChange={(e) => {
                            const selectedBank = bankAccounts.find(
                              (bank) => bank.id.toString() === e.target.value
                            );
                            setSilverBankAccount(selectedBank || null);
                          }}
                        >
                          <option value="">Select Bank</option>
                          {bankAccounts.map((bank) => (
                            <option
                              key={bank.id}
                              value={bank.id}
                              selected={silverBankAccount?.id === bank.id}
                            >
                              {bank.bankName}{" "}
                              {bank.accountNumber
                                ? `- ${bank.accountNumber}`
                                : ""}
                            </option>
                          ))}
                        </Form.Select>
                      </Col>
                    </Row>
                  )}
                </div>
              ))}
            </div>
          )}
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="primary"
          onClick={handleReceiveUpdate}
          disabled={!receiveDate || loading}
        >
          {loading ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              Submitting...
            </>
          ) : (
            <>Submit</>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default ReceiveUpdateModal;
